import { useState } from "react";

import { searchMeterByMsn } from "../services/electralink.service";
import { MeterDetails } from "../types/components.types";

const useMsnSearch = () => {
  // Set up state variables for the MSN search tab to track input, validation, loading, errors, and fetched meter details.
  const [msnInput, setMsnInput] = useState<string>("");
  const [postcodeInput, setPostcodeInput] = useState<string>("");
  const [isValidSearch, setIsValidSearch] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [meterDetails, setMeterDetails] = useState<MeterDetails | null>(null);

  const handleSearch = async () => {
    // Reset MSN validation and meter details state before running a new search to ensure that old results are not displayed while a new request is running.
    setIsValidSearch(null);
    setMeterDetails(null);

    // If the MSN input is empty, display an error message and return early.
    if (!msnInput.trim()) {
      setError("Please enter a MSN");
      return;
    }

    // If the postcode input is empty, display an error message and return early.
    if (!postcodeInput.trim()) {
      setError("Please enter a postcode");
      return;
    }

    // Clear any previous errors and display the 'loading' message on the UI.
    setError(null);
    setIsLoading(true);

    try {
      // Call searchMeterByMsn() with the user's inputs to validate and fetch meter details.
      const results = await searchMeterByMsn(msnInput, postcodeInput);

      // Update the state based on the validity of the field inputs.
      setIsValidSearch(results.isValid);

      // Display any error returned by the API, regardless of whether the format was valid
      // (e.g. a valid MSN/postcode that returns a 404 "no meter found" from the API).
      if (results.error) {
        setError(results.error);
        return;
      }

      // If the MSN search is valid and meter details exist, update the hook state with meter details.
      if (results.isValid && results.meterDetails) {
        setMeterDetails(results.meterDetails);
        return;
      }
    } catch (err) {
      // Display any unexpected errors from the API call.
      setError("An error occurred while searching. Please try again.");
      setIsValidSearch(false);
    } finally {
      // Reset the loading state once done.
      setIsLoading(false);
    }
  };

  return {
    msnInput,
    setMsnInput,
    postcodeInput,
    setPostcodeInput,
    isValidSearch,
    setIsValidSearch,
    error,
    setError,
    isLoading,
    setIsLoading,
    meterDetails,
    setMeterDetails,
    handleSearch,
  };
};

export default useMsnSearch;
